import * as A from '@core/arena'

import { Objects } from '@openforis/arena-core'

import * as ObjectUtils from '@core/objectUtils'
import * as Survey from '@core/survey/survey'

import { keys } from './userKeys'

export const keysPrefs = {
  surveys: 'surveys',
  current: 'current',
  language: 'language',
  notifyOnUserAccessRequest: 'notifyOnUserAccessRequest',
  whatsNewSeenIds: 'whatsNewSeenIds',
} as const

export const keysSurveyPrefs = {
  cycle: ObjectUtils.keys.cycle,
  language: 'language',
} as const

type SurveyId = string | number | null | undefined

const getPrefs = A.propOr({}, keys.prefs)

const pathSurveyCurrent = [keys.prefs, keysPrefs.surveys, keysPrefs.current]
const surveyPrefsPath = ({ surveyId }: { surveyId: SurveyId }) => [keys.prefs, keysPrefs.surveys, String(surveyId)]
const surveyPrefPath = ({ surveyId, key }: { surveyId: SurveyId; key: string }) => [
  ...surveyPrefsPath({ surveyId }),
  key,
]
const pathLanguage = [keys.prefs, keysPrefs.language]
const pathNotifyOnUserAccessRequest = [keys.prefs, keysPrefs.notifyOnUserAccessRequest]
const pathWhatsNewSeenIds = [keys.prefs, keysPrefs.whatsNewSeenIds]

const surveyCyclePrefPath = (surveyId: SurveyId) => surveyPrefPath({ surveyId, key: keysSurveyPrefs.cycle })
const surveyLangPrefPath = (surveyId: SurveyId) => surveyPrefPath({ surveyId, key: keysSurveyPrefs.language })

// ====== CREATE
export const newPrefs = ({
  surveyId = null,
  surveyCycleKey = null,
}: {
  surveyId?: SurveyId
  surveyCycleKey?: unknown
}) => {
  let tempUser = {}
  if (!Objects.isEmpty(surveyId)) {
    tempUser = assocPrefSurveyCurrent(surveyId)(tempUser)
  }
  if (!Objects.isEmpty(surveyCycleKey)) {
    tempUser = assocPrefSurveyCycle(surveyId, surveyCycleKey)(tempUser)
  }
  return getPrefs(tempUser)
}

// ====== READ
export const getPrefSurveyCurrent = A.path(pathSurveyCurrent)

export const getPrefSurveyCycle = (surveyId: SurveyId) => A.path(surveyCyclePrefPath(surveyId))
export const getPrefSurveyLang = (surveyId: SurveyId) => A.path(surveyLangPrefPath(surveyId))

export const getPrefSurveyCurrentCycle = (user: Record<string, unknown>) => {
  const surveyId = getPrefSurveyCurrent(user) as SurveyId
  return getPrefSurveyCycle(surveyId)(user)
}

export const getPrefSurveyCurrentLanguage = (user: Record<string, unknown>) => {
  const surveyId = getPrefSurveyCurrent(user) as SurveyId
  return getPrefSurveyLang(surveyId)(user)
}

export const getPrefLanguage = A.path(pathLanguage)

// defaults to true: an unset pref must behave like "notify", preserving pre-existing behaviour
export const getPrefNotifyOnUserAccessRequest = (user: Record<string, unknown>): boolean =>
  A.pathOr(true, pathNotifyOnUserAccessRequest, user) as boolean

// ids of the "What's new" items the user chose not to see again
export const getPrefWhatsNewSeenIds = (user: Record<string, unknown>): string[] =>
  A.pathOr([], pathWhatsNewSeenIds, user) as string[]

// ====== UPDATE
export const assocPrefSurveyCycle = (surveyId: SurveyId, cycle: unknown) =>
  A.assocPath(surveyCyclePrefPath(surveyId), cycle)
export const assocPrefSurveyLang = ({ surveyId, lang }: { surveyId: SurveyId; lang: string }) =>
  A.assocPath(surveyLangPrefPath(surveyId), lang)

export const assocPrefSurveyCurrent = (surveyId: SurveyId) => (user: Record<string, unknown>) =>
  A.pipe(
    // If the survey is selected for the first time, add the first cycle to its prefs
    A.when(A.always(A.isNil(getPrefSurveyCycle(surveyId)(user))), assocPrefSurveyCycle(surveyId, Survey.cycleOneKey)),
    A.assocPath(pathSurveyCurrent, surveyId)
  )(user)

export const assocPrefSurveyCurrentAndCycle = (surveyId: SurveyId, cycle: unknown) =>
  A.pipe(assocPrefSurveyCurrent(surveyId), assocPrefSurveyCycle(surveyId, cycle))

export const assocPrefLanguage = ({ lang }: { lang: string }) => A.assocPath(pathLanguage, lang)

export const assocPrefNotifyOnUserAccessRequest = (value: boolean) => A.assocPath(pathNotifyOnUserAccessRequest, value)

export const assocPrefWhatsNewSeenIds = (ids: string[]) => A.assocPath(pathWhatsNewSeenIds, ids)

// ====== DELETE
export const deletePrefSurvey = (surveyId: SurveyId) => (user: Record<string, unknown>) => {
  const surveyIdPref = getPrefSurveyCurrent(user)
  return A.pipe(
    A.when(A.always(String(surveyIdPref as SurveyId) === String(surveyId)), assocPrefSurveyCurrent(null)),
    A.dissocPath(surveyPrefsPath({ surveyId }))
  )(user)
}
